import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

const SESSION_API_URL = process.env.REACT_APP_SESSION_API_URL || 'https://hmsp.d-7.cc';
const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || 'wss://ws.d-7.cc';
const API_KEY = process.env.REACT_APP_API_KEY || 'd8uNa7qkuK1tEczzsGmdn7IB85eAEtgouEeuoHp0';

const TECH_PASS_HASH = 'bfced2c0'; // simple hash of the password
function simpleHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(16);
}

function App() {
  const [authenticated, setAuthenticated] = useState(() => sessionStorage.getItem('tech_auth') === 'true');
  const [passInput, setPassInput] = useState('');
  const [passError, setPassError] = useState('');
  const [session, setSession] = useState(() => {
    try { const s = sessionStorage.getItem('hc_session'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [connectionUrl, setConnectionUrl] = useState(() => sessionStorage.getItem('hc_connectionUrl') || '');
  const [sessionCode, setSessionCode] = useState(() => sessionStorage.getItem('hc_sessionCode') || '');
  const [technicianName, setTechnicianName] = useState(() => sessionStorage.getItem('hc_techName') || '');
  const [isConnected, setIsConnected] = useState(false);
  const [clientConnected, setClientConnected] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('Not connected');
  const [screenInfo, setScreenInfo] = useState(null);
  const [hasFrames, setHasFrames] = useState(false);
  const [fps, setFps] = useState(0);
  const [copied, setCopied] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const remoteDesktopRef = useRef(null);

  const canvasRef = useRef(null);
  const cursorCanvasRef = useRef(null);
  const screenContainerRef = useRef(null);
  const wsRef = useRef(null);
  const frameCountRef = useRef(0);
  const fpsIntervalRef = useRef(null);
  const cursorPosRef = useRef({ x: 0, y: 0 });
  const screenInfoRef = useRef(null);
  const canvasSizeRef = useRef({ w: 0, h: 0 });
  const mouseActiveRef = useRef(false);
  const mouseIdleTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);

  // FPS counter
  useEffect(() => {
    fpsIntervalRef.current = setInterval(() => {
      setFps(frameCountRef.current);
      frameCountRef.current = 0;
    }, 1000);
    return () => {
      clearInterval(fpsIntervalRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  // Draw remote cursor overlay (fades when technician is actively moving mouse)
  const drawCursor = useCallback((x, y) => {
    const canvas = cursorCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Don't draw remote cursor while technician is actively moving mouse
    if (mouseActiveRef.current) return;

    // Scale from stream coords to canvas display coords
    const info = screenInfoRef.current;
    if (!info) return;
    const displayX = (x / info.scaledWidth) * canvas.width;
    const displayY = (y / info.scaledHeight) * canvas.height;

    // Draw arrow cursor
    ctx.save();
    ctx.translate(displayX, displayY);
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = 'white';
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, 18);
    ctx.lineTo(5, 14);
    ctx.lineTo(9, 22);
    ctx.lineTo(12, 21);
    ctx.lineTo(8, 13);
    ctx.lineTo(14, 13);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }, []);

  // Render a tile onto the main canvas
  const renderTile = useCallback((tileData, col, row, cols, rows, canvasWidth, canvasHeight) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const tileW = Math.floor(canvasWidth / cols);
    const tileH = Math.floor(canvasHeight / rows);
    const x = col * tileW;
    const y = row * tileH;
    const w = col === cols - 1 ? canvasWidth - x : tileW;
    const h = row === rows - 1 ? canvasHeight - y : tileH;

    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, x, y, w, h);
    };
    img.src = `data:image/webp;base64,${tileData}`;
  }, []);

  const parseTokenFromConnectionUrl = (url) => {
    const parts = url.split('/session/');
    return parts.length > 1 ? parts[1] : '';
  };

  // Session code is now returned directly from the API

  const setupWebSocket = useCallback((sessionId, technicianToken) => {
    const ws = new WebSocket(WEBSOCKET_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatusMessage('WebSocket connected, joining session...');
      ws.send(JSON.stringify({
        type: 'join-technician',
        data: { sessionId, technicianToken },
      }));
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        switch (message.type) {
          case 'joined-session':
            reconnectAttemptsRef.current = 0;
            setIsConnected(true);
            setStatusMessage('Joined session — waiting for client agent');
            break;
          case 'client-connected':
            setClientConnected(true);
            setStatusMessage('Client agent connected — waiting for screen stream');
            break;
          case 'client-disconnected':
          case 'disconnect':
            setClientConnected(false);
            setHasFrames(false);
            setScreenInfo(null);
            screenInfoRef.current = null;
            setStatusMessage('Client disconnected');
            break;
          case 'screen-info': {
            const info = message.data;
            setScreenInfo(info);
            screenInfoRef.current = info;
            setStatusMessage('Receiving screen stream');
            // Initialize canvas size
            const canvas = canvasRef.current;
            const cursorCanvas = cursorCanvasRef.current;
            if (canvas) {
              canvas.width = info.scaledWidth;
              canvas.height = info.scaledHeight;
              canvasSizeRef.current = { w: info.scaledWidth, h: info.scaledHeight };
            }
            if (cursorCanvas) {
              cursorCanvas.width = info.scaledWidth;
              cursorCanvas.height = info.scaledHeight;
            }
            break;
          }
          case 'screen-data':
            // Legacy full-frame support
            frameCountRef.current++;
            setHasFrames(true);
            if (canvasRef.current && message.data.frame) {
              const ctx = canvasRef.current.getContext('2d');
              const img = new Image();
              img.onload = () => ctx.drawImage(img, 0, 0, canvasRef.current.width, canvasRef.current.height);
              img.src = `data:image/jpeg;base64,${message.data.frame}`;
            }
            break;
          case 'screen-tiles': {
            frameCountRef.current++;
            setHasFrames(true);
            const { tiles, width, height, cols, rows } = message.data;
            const canvas = canvasRef.current;
            if (canvas && (canvas.width !== width || canvas.height !== height)) {
              canvas.width = width;
              canvas.height = height;
              canvasSizeRef.current = { w: width, h: height };
              if (cursorCanvasRef.current) {
                cursorCanvasRef.current.width = width;
                cursorCanvasRef.current.height = height;
              }
            }
            if (tiles) {
              for (const tile of tiles) {
                renderTile(tile.d, tile.c, tile.r, cols, rows, width, height);
              }
            }
            break;
          }
          case 'cursor-pos':
            cursorPosRef.current = { x: message.data.x, y: message.data.y };
            drawCursor(message.data.x, message.data.y);
            break;
          case 'error':
            setError(message.message || 'Connection error');
            setStatusMessage('Error');
            break;
          default:
            break;
        }
      } catch (e) {
        // ignore parse errors
      }
    };

    ws.onerror = () => {
      setStatusMessage('WebSocket error — reconnecting...');
    };

    ws.onclose = () => {
      setIsConnected(false);
      setClientConnected(false);
      // Auto-reconnect with backoff
      const attempt = (reconnectAttemptsRef.current || 0) + 1;
      reconnectAttemptsRef.current = attempt;
      if (attempt <= 20) {
        const delay = Math.min(1000 * Math.pow(1.5, attempt - 1), 30000);
        setStatusMessage(`Disconnected — reconnecting in ${Math.round(delay / 1000)}s (attempt ${attempt}/20)...`);
        reconnectTimerRef.current = setTimeout(() => {
          setupWebSocket(sessionId, technicianToken);
        }, delay);
      } else {
        setStatusMessage('Disconnected — auto-reconnect stopped. Click Reconnect to try again.');
      }
    };
  }, [renderTile, drawCursor]);

  // Auto-rejoin persisted session on page load (survives refresh)
  const hasAutoRejoined = useRef(false);
  useEffect(() => {
    if (hasAutoRejoined.current) return;
    hasAutoRejoined.current = true;
    const savedSession = sessionStorage.getItem('hc_session');
    const savedToken = sessionStorage.getItem('hc_token');
    if (savedSession && savedToken && authenticated) {
      try {
        const s = JSON.parse(savedSession);
        setStatusMessage('Reconnecting to previous session...');
        setupWebSocket(s.sessionId, savedToken);
      } catch {}
    }
  }, [authenticated, setupWebSocket]);

  const sendInput = useCallback((type, data) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type, data }));
    }
  }, []);

  const getScaledCoords = useCallback((e) => {
    const container = screenContainerRef.current;
    if (!container || !screenInfoRef.current) return null;
    const rect = container.getBoundingClientRect();
    const info = screenInfoRef.current;
    const scaleX = info.scaledWidth / rect.width;
    const scaleY = info.scaledHeight / rect.height;
    return {
      x: Math.round((e.clientX - rect.left) * scaleX),
      y: Math.round((e.clientY - rect.top) * scaleY),
    };
  }, []);

  const handleMouseMove = useCallback((e) => {
    const c = getScaledCoords(e);
    if (c) sendInput('mouse-event', { type: 'move', ...c });

    // Hide remote cursor while technician is actively moving
    mouseActiveRef.current = true;
    const cursorCanvas = cursorCanvasRef.current;
    if (cursorCanvas) {
      const ctx = cursorCanvas.getContext('2d');
      ctx.clearRect(0, 0, cursorCanvas.width, cursorCanvas.height);
    }
    clearTimeout(mouseIdleTimerRef.current);
    mouseIdleTimerRef.current = setTimeout(() => {
      mouseActiveRef.current = false;
    }, 300);
  }, [getScaledCoords, sendInput]);

  const handleClick = useCallback((e) => {
    const c = getScaledCoords(e);
    if (c) sendInput('mouse-event', { type: 'click', ...c, button: e.button });
  }, [getScaledCoords, sendInput]);

  const handleDblClick = useCallback((e) => {
    const c = getScaledCoords(e);
    if (c) sendInput('mouse-event', { type: 'dblclick', ...c });
  }, [getScaledCoords, sendInput]);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    const c = getScaledCoords(e);
    if (c) sendInput('mouse-event', { type: 'rightclick', ...c });
  }, [getScaledCoords, sendInput]);

  const handleScroll = useCallback((e) => {
    const c = getScaledCoords(e);
    if (c) {
      const delta = e.deltaY > 0 ? -3 : 3;
      sendInput('mouse-event', { type: 'scroll', ...c, delta });
    }
  }, [getScaledCoords, sendInput]);

  const handleKeyDown = useCallback((e) => {
    // Intercept Ctrl+V for clipboard paste
    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        if (text) sendInput('clipboard-paste', { text });
      }).catch(() => {});
      return;
    }
    e.preventDefault();
    sendInput('keyboard-event', {
      type: 'keydown',
      key: e.key,
      code: e.code,
      shift: e.shiftKey,
      ctrl: e.ctrlKey,
      alt: e.altKey,
      meta: e.metaKey,
    });
  }, [sendInput]);

  const handleKeyUp = useCallback((e) => {
    e.preventDefault();
    sendInput('keyboard-event', {
      type: 'keyup',
      key: e.key,
      code: e.code,
      shift: e.shiftKey,
      ctrl: e.ctrlKey,
      alt: e.altKey,
      meta: e.metaKey,
    });
  }, [sendInput]);

  const sendCommand = useCallback((cmd) => {
    sendInput('special-command', { command: cmd });
  }, [sendInput]);

  const toggleFullscreen = useCallback(() => {
    const el = remoteDesktopRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const zoomIn = useCallback(() => setZoom(z => Math.min(z + 0.25, 3)), []);
  const zoomOut = useCallback(() => setZoom(z => Math.max(z - 0.25, 0.5)), []);
  const zoomReset = useCallback(() => setZoom(1), []);

  const createSession = async () => {
    if (!technicianName.trim()) {
      setError('Please enter your name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${SESSION_API_URL}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({
          technicianName: technicianName.trim(),
          expirationMinutes: 60,
        }),
      });

      if (!response.ok) throw new Error('Failed to create session');

      const data = await response.json();
      setSession(data);
      const token = parseTokenFromConnectionUrl(data.connectionUrl);
      const code = data.sessionCode || token.substring(0, 6).toUpperCase();
      const url = `https://connect.d-7.cc/support.html?code=${code}`;
      setSessionCode(code);
      setConnectionUrl(url);

      // Persist for reconnect across page refresh
      sessionStorage.setItem('hc_session', JSON.stringify(data));
      sessionStorage.setItem('hc_sessionCode', code);
      sessionStorage.setItem('hc_connectionUrl', url);
      sessionStorage.setItem('hc_token', token);
      sessionStorage.setItem('hc_techName', technicianName.trim());

      if (!token) throw new Error('Missing session token');
      if (wsRef.current) wsRef.current.close();
      setupWebSocket(data.sessionId, token);
    } catch (err) {
      setError('Failed to create session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text || connectionUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [rejoinCode, setRejoinCode] = useState('');
  const [rejoinLoading, setRejoinLoading] = useState(false);

  const rejoinByCode = async () => {
    const code = rejoinCode.trim().toUpperCase();
    if (code.length < 6) {
      setError('Please enter a valid 6-character session code.');
      return;
    }
    setRejoinLoading(true);
    setError('');
    try {
      const res = await fetch(`${SESSION_API_URL}/session/${code}`, {
        headers: { 'x-api-key': API_KEY },
      });
      if (!res.ok) throw new Error(res.status === 404 ? 'Session not found' : 'Server error');
      const data = await res.json();
      if (!data.valid) throw new Error('Session expired or invalid');

      const token = data.connectionToken;
      const sessionObj = { sessionId: data.sessionId, connectionUrl: `${SESSION_API_URL}/session/${token}`, sessionCode: code };
      const url = `https://connect.d-7.cc/support.html?code=${code}`;

      setSession(sessionObj);
      setSessionCode(code);
      setConnectionUrl(url);

      // Persist for reconnect
      sessionStorage.setItem('hc_session', JSON.stringify(sessionObj));
      sessionStorage.setItem('hc_token', token);
      sessionStorage.setItem('hc_sessionCode', code);
      sessionStorage.setItem('hc_connectionUrl', url);
      sessionStorage.setItem('hc_techName', data.technicianName || technicianName || '');

      reconnectAttemptsRef.current = 0;
      if (wsRef.current) { try { wsRef.current.close(); } catch {} }
      setupWebSocket(data.sessionId, token);
    } catch (err) {
      setError(err.message || 'Failed to rejoin session.');
    } finally {
      setRejoinLoading(false);
    }
  };

  const endSession = () => {
    reconnectAttemptsRef.current = 99; // prevent auto-reconnect
    clearTimeout(reconnectTimerRef.current);
    if (wsRef.current) wsRef.current.close();
    wsRef.current = null;
    setSession(null);
    setConnectionUrl('');
    setSessionCode('');
    setIsConnected(false);
    setClientConnected(false);
    setHasFrames(false);
    setScreenInfo(null);
    screenInfoRef.current = null;
    setStatusMessage('Not connected');
    // Clear persisted session
    sessionStorage.removeItem('hc_session');
    sessionStorage.removeItem('hc_sessionCode');
    sessionStorage.removeItem('hc_connectionUrl');
    sessionStorage.removeItem('hc_token');
    sessionStorage.removeItem('hc_techName');
  };

  const rejoinSession = useCallback(() => {
    const savedSession = session || (() => {
      try { return JSON.parse(sessionStorage.getItem('hc_session')); } catch { return null; }
    })();
    const savedToken = sessionStorage.getItem('hc_token');
    if (!savedSession || !savedToken) {
      setError('No session to rejoin. Please create a new session.');
      return;
    }
    reconnectAttemptsRef.current = 0;
    clearTimeout(reconnectTimerRef.current);
    if (wsRef.current) { try { wsRef.current.close(); } catch {} }
    setError('');
    setStatusMessage('Reconnecting...');
    setHasFrames(false);
    setupWebSocket(savedSession.sessionId, savedToken);
  }, [session, setupWebSocket]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (simpleHash(passInput) === TECH_PASS_HASH) {
      sessionStorage.setItem('tech_auth', 'true');
      setAuthenticated(true);
      setPassError('');
    } else {
      setPassError('Incorrect password');
    }
  };

  if (!authenticated) {
    return (
      <div className="App">
        <header className="App-header">
          <img src="/logo.png" alt="Humanity MSP" style={{maxWidth: '220px', height: 'auto', marginBottom: '8px'}} />
          <h1>Humanity Connect</h1>
          <p>Technician Login</p>
        </header>
        <main className="main-content">
          <div className="session-creator">
            <h2>Enter Technician Password</h2>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <input
                  type="password"
                  value={passInput}
                  onChange={(e) => { setPassInput(e.target.value); setPassError(''); }}
                  placeholder="Password"
                  autoFocus
                />
              </div>
              {passError && <p style={{color: '#e74c3c', marginBottom: '12px'}}>{passError}</p>}
              <button type="submit" className="btn-primary">Login</button>
            </form>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <img src="/logo.png" alt="Humanity MSP" style={{maxWidth: '220px', height: 'auto', marginBottom: '8px'}} />
        <h1>Humanity Connect</h1>
        <p>Secure Remote Support Dashboard</p>
      </header>

      <main className="main-content">
        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')} className="close-btn">x</button>
          </div>
        )}

        {!session ? (
          <div className="session-creator">
            <h2>Create New Support Session</h2>
            <div className="form-group">
              <label htmlFor="technicianName">Your Name:</label>
              <input
                type="text"
                id="technicianName"
                value={technicianName}
                onChange={(e) => setTechnicianName(e.target.value)}
                placeholder="Enter your name"
                maxLength={50}
                onKeyDown={(e) => e.key === 'Enter' && createSession()}
              />
            </div>
            <button onClick={createSession} disabled={loading} className="btn-primary">
              {loading ? 'Creating...' : 'Create Session'}
            </button>

            <div style={{margin: '28px 0 16px', borderTop: '1px solid #e2e8f0', paddingTop: '24px'}}>
              <h2 style={{fontSize: '18px', marginBottom: '12px'}}>Rejoin Existing Session</h2>
              <div className="form-group">
                <label htmlFor="rejoinCode">Session Code:</label>
                <input
                  type="text"
                  id="rejoinCode"
                  value={rejoinCode}
                  onChange={(e) => setRejoinCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6))}
                  placeholder="e.g. A1B2C3"
                  maxLength={6}
                  style={{fontFamily: 'monospace', letterSpacing: '2px', textTransform: 'uppercase'}}
                  onKeyDown={(e) => e.key === 'Enter' && rejoinByCode()}
                />
              </div>
              <button onClick={rejoinByCode} disabled={rejoinLoading || rejoinCode.length < 6} className="btn-primary" style={{background: '#00bfa5'}}>
                {rejoinLoading ? 'Connecting...' : 'Rejoin Session'}
              </button>
            </div>
          </div>
        ) : (
          <div className="session-active">
            <div className="session-info-bar">
              <span className={`status-dot ${clientConnected ? 'green' : isConnected ? 'yellow' : 'red'}`} />
              <span className="status-text">{statusMessage}</span>
              {hasFrames && <span className="fps-badge">{fps} FPS</span>}
              <button onClick={() => copyToClipboard()} className="btn-small">{copied ? 'Copied!' : 'Copy Link'}</button>
              {!isConnected && <button onClick={rejoinSession} className="btn-small btn-reconnect-small">Reconnect</button>}
              <button onClick={endSession} className="btn-small btn-danger-small">End</button>
            </div>

            {!hasFrames ? (
              <div className="waiting-panel">
                <h2>Waiting for Client</h2>

                <div className="session-code-section">
                  <p className="session-code-label">Session Code:</p>
                  <div className="session-code-display" onClick={() => copyToClipboard(sessionCode)}>
                    {sessionCode}
                  </div>
                </div>

                <div className="url-container">
                  <input type="text" value={connectionUrl} readOnly className="url-input" />
                  <button onClick={() => copyToClipboard()} className="btn-copy">{copied ? 'Copied!' : 'Copy'}</button>
                </div>

                <div className="instructions">
                  <h3>Send this link to the client:</h3>
                  <ol>
                    <li>Copy the link above and send it to the client</li>
                    <li>Client clicks the link &mdash; download starts automatically</li>
                    <li>Client opens the downloaded file to connect</li>
                  </ol>
                </div>
              </div>
            ) : (
              <div className="remote-desktop" ref={remoteDesktopRef}>
                <div className="command-toolbar">
                  <button onClick={() => sendCommand('ctrl-alt-del')} className="cmd-btn" title="Ctrl+Alt+Delete">Ctrl+Alt+Del</button>
                  <button onClick={() => sendCommand('alt-tab')} className="cmd-btn" title="Alt+Tab">Alt+Tab</button>
                  <button onClick={() => sendCommand('win')} className="cmd-btn" title="Windows Key">⊞ Win</button>
                  <button onClick={() => sendCommand('alt-f4')} className="cmd-btn" title="Alt+F4">Alt+F4</button>
                  <button onClick={() => sendCommand('ctrl-shift-esc')} className="cmd-btn" title="Task Manager">Task Mgr</button>
                  <button onClick={() => sendCommand('win-r')} className="cmd-btn" title="Run Dialog">Win+R</button>
                  <button onClick={() => sendCommand('win-e')} className="cmd-btn" title="File Explorer">Explorer</button>
                  <button onClick={() => {
                    navigator.clipboard.readText().then(text => {
                      if (text) sendInput('clipboard-paste', { text });
                    }).catch(() => setError('Clipboard access denied'));
                  }} className="cmd-btn cmd-paste" title="Paste clipboard to remote">📋 Paste</button>
                  <span className="toolbar-separator" />
                  <button onClick={zoomOut} className="cmd-btn" title="Zoom Out">−</button>
                  <button onClick={zoomReset} className="cmd-btn cmd-zoom-label" title="Reset Zoom">{Math.round(zoom * 100)}%</button>
                  <button onClick={zoomIn} className="cmd-btn" title="Zoom In">+</button>
                  <button onClick={toggleFullscreen} className="cmd-btn cmd-fullscreen" title="Toggle Fullscreen">{isFullscreen ? '⮌ Exit' : '⛶ Fullscreen'}</button>
                </div>
                <div className="screen-viewer-wrapper" style={{ overflow: zoom > 1 ? 'auto' : 'hidden' }}>
                  <div
                    ref={screenContainerRef}
                    className="screen-viewer"
                    tabIndex={0}
                    onMouseMove={handleMouseMove}
                    onClick={handleClick}
                    onDoubleClick={handleDblClick}
                    onContextMenu={handleContextMenu}
                    onWheel={handleScroll}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyUp}
                    style={{ cursor: 'default', position: 'relative', width: `${zoom * 100}%`, transformOrigin: 'top left' }}
                  >
                  <canvas
                    ref={canvasRef}
                    style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
                  />
                  <canvas
                    ref={cursorCanvasRef}
                    style={{
                      position: 'absolute', top: 0, left: 0,
                      width: '100%', height: '100%',
                      pointerEvents: 'none',
                    }}
                  />
                  </div>
                </div>
                {screenInfo && (
                  <div className="screen-meta">
                    Client: {screenInfo.width}x{screenInfo.height} | Stream: {screenInfo.scaledWidth}x{screenInfo.scaledHeight} | Tiles: {screenInfo.tileCols || '?'}x{screenInfo.tileRows || '?'} | {fps} FPS
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
